import { useCallback, useEffect, useState } from 'react'

// reCAPTCHA v3 site key from environment
const RECAPTCHA_SITE_KEY = (import.meta as any).env.VITE_RECAPTCHA_SITE_KEY || ''

// Type declarations for grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

export type RecaptchaAction = 'login' | 'register' | 'signup'

interface UseRecaptchaOptions {
  action: RecaptchaAction
}

interface UseRecaptchaReturn {
  executeRecaptcha: () => Promise<string | null>
  isReady: boolean
  isEnabled: boolean
  error: string | null
}

/**
 * Hook to generate reCAPTCHA v3 tokens for form submissions
 *
 * @param options - Configuration options including the action name
 * @returns Object with executeRecaptcha function and status flags
 *
 * @example
 * ```tsx
 * const { executeRecaptcha, isReady, isEnabled } = useRecaptcha({ action: 'login' })
 *
 * const handleSubmit = async (data) => {
 *   const token = await executeRecaptcha()
 *   await api.login({ ...data, recaptcha_token: token })
 * }
 * ```
 */
export function useRecaptcha({ action }: UseRecaptchaOptions): UseRecaptchaReturn {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEnabled = Boolean(RECAPTCHA_SITE_KEY)

  useEffect(() => {
    if (!isEnabled) {
      // reCAPTCHA is not configured, skip initialization
      return
    }

    // Check if grecaptcha is already loaded
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        setIsReady(true)
      })
      return
    }

    // Wait for the script to load
    const checkReady = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setIsReady(true)
        })
        return true
      }
      return false
    }

    // Poll for grecaptcha to be available
    const interval = setInterval(() => {
      if (checkReady()) {
        clearInterval(interval)
      }
    }, 100)

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval)
      if (!isReady) {
        setError('reCAPTCHA failed to load')
      }
    }, 10000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [isEnabled, isReady])

  const executeRecaptcha = useCallback(async (): Promise<string | null> => {
    if (!isEnabled) {
      // reCAPTCHA is not configured, return null (backend should handle this gracefully)
      return null
    }

    if (!isReady || !window.grecaptcha) {
      console.warn('reCAPTCHA not ready yet')
      return null
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
      return token
    } catch (err) {
      console.error('Failed to execute reCAPTCHA:', err)
      setError('Failed to verify you are human')
      return null
    }
  }, [action, isReady, isEnabled])

  return {
    executeRecaptcha,
    isReady,
    isEnabled,
    error,
  }
}

/**
 * Parse Retry-After header value and return human-readable time
 *
 * @param retryAfter - Value from Retry-After header (seconds)
 * @returns Human-readable string like "30 minutes" or "45 seconds"
 */
export function parseRetryAfter(retryAfter: string | number | null): string {
  if (!retryAfter) return 'a few minutes'

  const seconds = typeof retryAfter === 'string' ? parseInt(retryAfter, 10) : retryAfter

  if (isNaN(seconds)) return 'a few minutes'

  if (seconds >= 3600) {
    const hours = Math.ceil(seconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''}`
  }

  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''}`
  }

  return `${seconds} second${seconds > 1 ? 's' : ''}`
}

/**
 * Rate limit error details extracted from API response
 */
export interface RateLimitError {
  isRateLimited: boolean
  retryAfter: number | null
  retryAfterText: string
  isAccountLocked: boolean
  message: string
}

/**
 * Extract rate limit information from an error response
 *
 * @param error - Error object from API call
 * @returns RateLimitError object with parsed information
 */
export function extractRateLimitError(error: any): RateLimitError {
  const defaultResult: RateLimitError = {
    isRateLimited: false,
    retryAfter: null,
    retryAfterText: '',
    isAccountLocked: false,
    message: error?.message || 'An error occurred',
  }

  // Check for 429 status
  const status = error?.response?.status || error?.status
  if (status !== 429) {
    return defaultResult
  }

  // Extract Retry-After header
  const headers = error?.response?.headers || {}
  const retryAfter = headers['retry-after'] || headers['Retry-After']
  const retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : null

  // Check if this is an account lockout
  const detail = error?.response?.data?.detail || ''
  const isAccountLocked = detail.toLowerCase().includes('locked') ||
                          detail.toLowerCase().includes('multiple failed')

  // Build user-friendly message
  let message: string
  if (isAccountLocked) {
    message = `Account temporarily locked. Try again in ${parseRetryAfter(retryAfterSeconds)}.`
  } else {
    message = `Too many requests. Try again in ${parseRetryAfter(retryAfterSeconds)}.`
  }

  return {
    isRateLimited: true,
    retryAfter: retryAfterSeconds,
    retryAfterText: parseRetryAfter(retryAfterSeconds),
    isAccountLocked,
    message,
  }
}

/**
 * Check if an error is a reCAPTCHA verification failure
 *
 * @param error - Error object from API call
 * @returns true if the error is due to reCAPTCHA verification failure
 */
export function isRecaptchaError(error: any): boolean {
  const status = error?.response?.status || error?.status
  const detail = error?.response?.data?.detail || error?.message || ''

  return status === 400 && (
    detail.toLowerCase().includes('recaptcha') ||
    detail.toLowerCase().includes('verification failed')
  )
}
